/**
 * Phase 138A/B — StreamX Notification Bell
 * --------------------------------------
 * In-product billing awareness inbox. Surfaces every payment, invoice,
 * suspension, reactivation, and replay-archive event so the dashboard
 * feels alive instead of silent.
 *
 * Doctrine: celebratory + informative, NOT ERP alerts.
 *   • Unread states get a stronger left-accent + glow.
 *   • Click → navigate to the notification's `url` (deep-link to invoice, replays, etc.).
 *   • Auto-polls every 60s.
 *   • "Mark all as read" action.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import {
  Bell, Check, CheckCheck, Loader2, Receipt, CreditCard, Crown,
  AlertTriangle, Film, ShieldCheck, Sparkles, Trophy, Video, X,
} from 'lucide-react';

const POLL_INTERVAL_MS = 60_000;

const TYPE_META = {
  payment_received:        { Icon: Crown,           accent: '#22c55e', emoji: '🎉' },
  invoice_dispatched:      { Icon: Receipt,         accent: '#f97316', emoji: '📄' },
  invoice_overdue:         { Icon: AlertTriangle,   accent: '#ef4444', emoji: '⚠' },
  subscription_suspended:  { Icon: AlertTriangle,   accent: '#dc2626', emoji: '🔒' },
  subscription_restored:   { Icon: ShieldCheck,     accent: '#22c55e', emoji: '🎉' },
  replay_extended:         { Icon: Film,            accent: '#3b82f6', emoji: '📼' },
  replay_expiring:         { Icon: Film,            accent: '#f59e0b', emoji: '⏳' },
  replay_ready:            { Icon: Video,           accent: '#6366f1', emoji: '🎥' },
  featured_this_week:      { Icon: Trophy,          accent: '#fbbf24', emoji: '🏆' },
  story_of_the_week:       { Icon: Sparkles,        accent: '#fbbf24', emoji: '✨' },
  default:                 { Icon: Sparkles,        accent: '#FF5A1F', emoji: '✨' },
};

function metaFor(type) {
  return TYPE_META[type] || TYPE_META.default;
}

function fmtRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

export default function LivexNotificationBell({ className = '' }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const containerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/livex/me/notifications?limit=30');
      setItems(data?.entries || []);
      setUnread(data?.unread_count || 0);
    } catch (_) {
      // silent — bell should never break the surrounding page
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial + polling
  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleClick = async (n) => {
    setOpen(false);
    try {
      if (!n.read) {
        await api.post(`/livex/me/notifications/${n.id}/read`);
        setUnread((u) => Math.max(0, u - 1));
      }
    } catch (_) { /* silent */ }
    if (n.url) navigate(n.url);
  };

  const markAllRead = async () => {
    setMarking(true);
    try {
      await api.post('/livex/me/notifications/read-all');
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (_) { /* silent */ } finally {
      setMarking(false);
    }
  };

  const badge = unread > 9 ? '9+' : String(unread);

  return (
    <div ref={containerRef} className={`relative ${className}`} data-testid="livex-notification-bell-wrapper">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
        data-testid="livex-notification-bell"
        className="relative inline-flex items-center justify-center h-10 w-10 rounded-xl border-2 border-[#22314F] bg-[#07111F] hover:border-[#FF5A1F] transition-all hover:-translate-y-0.5"
        style={unread > 0 ? { boxShadow: '0 0 18px rgba(255,90,31,0.18)' } : undefined}
      >
        <Bell className="h-4 w-4 text-white" />
        {unread > 0 && (
          <span
            data-testid="livex-notification-bell-badge"
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 grid place-items-center text-[10px] font-black uppercase rounded-full text-white tabular-nums"
            style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', boxShadow: '0 4px 10px rgba(220,38,38,0.45)' }}
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="livex-notification-bell-panel"
          className="absolute right-0 mt-2 w-[min(380px,calc(100vw-32px))] rounded-2xl border-2 border-[#22314F] backdrop-blur-md text-white overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(7,17,31,0.98), rgba(10,10,15,0.98))',
            boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
            zIndex: 60,
          }}
          role="dialog"
          aria-label="Notifications panel"
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between border-b-2 border-[#22314F] bg-[#07111F]/80">
            <div>
              <div className="text-[11px] font-black tracking-[0.18em] uppercase text-[#FF5A1F]">StreamX · Inbox</div>
              <div className="text-sm font-black text-white mt-0.5">
                {unread > 0 ? `${unread} new event${unread === 1 ? '' : 's'}` : 'You are all caught up'}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  disabled={marking}
                  data-testid="livex-notification-bell-mark-all-read"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-[0.1em] text-[#94A3B8] hover:text-white transition-colors rounded-md hover:bg-white/5 disabled:opacity-50"
                >
                  {marking ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3 w-3" />}
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                aria-label="Close notifications"
                data-testid="livex-notification-bell-close"
                className="inline-flex items-center justify-center h-7 w-7 rounded-md text-[#94A3B8] hover:text-white hover:bg-white/5 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto" data-testid="livex-notification-bell-list">
            {loading && items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm font-bold text-[#94A3B8]" data-testid="livex-notification-bell-loading">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center" data-testid="livex-notification-bell-empty">
                <Bell className="h-7 w-7 text-[#22314F] mx-auto mb-2" />
                <div className="text-sm font-black text-white">No notifications yet</div>
                <div className="text-[12px] text-[#94A3B8] mt-1 font-medium">
                  Payment receipts, invoice updates, and replay events appear here.
                </div>
              </div>
            ) : (
              items.map((n) => {
                const m = metaFor(n.type);
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    data-testid={`livex-notification-item-${n.id}`}
                    data-notification-type={n.type}
                    data-read={n.read ? 'true' : 'false'}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-[#22314F]/50 last:border-b-0 transition-colors hover:bg-white/5 ${n.read ? '' : 'bg-[#0c1a2e]/40'}`}
                    style={n.read ? undefined : { borderLeft: `3px solid ${m.accent}` }}
                  >
                    <span
                      className="h-9 w-9 rounded-xl grid place-items-center flex-shrink-0 text-lg"
                      style={{
                        background: `linear-gradient(135deg, ${m.accent}, ${m.accent}aa)`,
                        boxShadow: n.read ? 'none' : `0 6px 16px ${m.accent}33`,
                      }}
                      aria-hidden
                    >
                      {m.emoji}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm leading-snug tracking-tight ${n.read ? 'text-[#cbd5e1] font-bold' : 'text-white font-black'}`}>
                        {n.title}
                      </div>
                      <div className="text-[12px] font-medium text-[#94A3B8] mt-0.5 leading-snug line-clamp-2">
                        {n.body}
                      </div>
                      <div className="text-[10px] font-black uppercase tracking-[0.1em] text-[#64748b] mt-1">
                        {fmtRelative(n.created_at)}
                      </div>
                    </div>
                    {!n.read && (
                      <span className="mt-1.5 h-2 w-2 rounded-full flex-shrink-0" style={{ background: m.accent }} aria-label="Unread" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
